import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import {
  Calendar, X, Loader2, Video, CheckCircle2,
  ExternalLink, Copy
} from 'lucide-react';

/**
 * InterviewScheduleModal
 *
 * Props:
 *   app          — application object (id, applicant, job_title)
 *   onClose      — close callback
 *   onScheduled  — callback(updatedApp) after successful schedule
 */
export function InterviewScheduleModal({ app, onClose, onScheduled }) {
  const { api } = useAuth();
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [interviewDate, setInterviewDate]   = useState('');
  const [durationMins,  setDurationMins]    = useState(60);
  const [notes,         setNotes]           = useState('');
  const [provider,      setProvider]        = useState(null); // 'google' | 'microsoft' | null
  const [saving,        setSaving]          = useState(false);
  const [result,        setResult]          = useState(null);

  useEffect(() => {
    api.get('/calendar/status')
      .then(r => {
        setCalendarStatus(r.data);
        // Auto-select first connected provider
        if (r.data.google?.connected) setProvider('google');
        else if (r.data.microsoft?.connected) setProvider('microsoft');
      })
      .catch(() => {});
  }, []);

  const schedule = async () => {
    if (!interviewDate) { toast.error('Please select a date and time'); return; }
    setSaving(true);
    try {
      const res = await api.post(`/calendar/schedule-interview/${app.id}`, {
        interview_date: interviewDate,
        duration_mins:  durationMins,
        notes,
        provider: provider || null,
      });
      setResult(res.data);
      onScheduled?.({ ...app, status: 'interview', interview_date: interviewDate });
      toast.success('Interview scheduled!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to schedule interview');
    } finally {
      setSaving(false);
    }
  };

  const PROVIDERS = [
    { key: null,        label: 'No calendar',      icon: '📋' },
    { key: 'google',    label: 'Google Calendar',   icon: '🗓️', configKey: 'google' },
    { key: 'microsoft', label: 'Outlook / Teams',   icon: '📅', configKey: 'microsoft' },
  ];

  if (result) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 text-center">
          <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-4" />
          <h3 className="font-bold text-xl mb-2">Interview Scheduled!</h3>
          <p className="text-muted-foreground text-sm mb-4">
            Candidate and employer have been notified by email
            {app.applicant?.phone ? ', SMS,' : ''} and push notification.
          </p>
          {result.meet_link && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
              <p className="text-xs font-medium text-blue-700 mb-1">
                {result.calendar_provider === 'google' ? 'Google Meet Link' : 'Teams Meeting Link'}
              </p>
              <div className="flex items-center gap-2">
                <a href={result.meet_link} target="_blank" rel="noopener noreferrer"
                  className="text-sm text-blue-600 truncate flex-1 hover:underline">
                  {result.meet_link}
                </a>
                <button onClick={() => { navigator.clipboard.writeText(result.meet_link); toast.success('Copied!'); }}>
                  <Copy className="w-4 h-4 text-blue-500" />
                </button>
              </div>
            </div>
          )}
          <Button className="w-full bg-[#003366]" onClick={onClose}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#003366]" />
            <h3 className="font-bold text-lg">Schedule Interview</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground mb-5">
          Scheduling for <span className="font-semibold text-foreground">{app.applicant?.full_name}</span>
          {' '}— {app.job_title}
        </p>

        <div className="space-y-4">
          {/* Date/time */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Date & Time *</label>
            <Input type="datetime-local" value={interviewDate} onChange={e => setInterviewDate(e.target.value)} className="h-10" />
          </div>

          {/* Duration */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Duration</label>
            <div className="flex gap-2">
              {[30, 45, 60, 90].map(d => (
                <button
                  key={d}
                  onClick={() => setDurationMins(d)}
                  className={`flex-1 py-2 text-sm rounded-lg border font-medium transition-all ${durationMins === d ? 'bg-[#003366] text-white border-[#003366]' : 'border-gray-200 hover:border-[#003366]/50'}`}
                >
                  {d}m
                </button>
              ))}
            </div>
          </div>

          {/* Calendar provider */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Calendar</label>
            <div className="grid grid-cols-3 gap-2">
              {PROVIDERS.map(p => {
                const cal = p.configKey ? calendarStatus?.[p.configKey] : null;
                const available = !p.configKey || cal?.connected;
                return (
                  <button
                    key={String(p.key)}
                    onClick={() => available && setProvider(p.key)}
                    disabled={!available}
                    className={`py-2.5 px-2 text-xs rounded-xl border font-medium transition-all flex flex-col items-center gap-1 ${
                      provider === p.key
                        ? 'bg-[#003366] text-white border-[#003366]'
                        : available
                          ? 'border-gray-200 hover:border-[#003366]/50'
                          : 'border-gray-100 text-gray-300 cursor-not-allowed'
                    }`}
                    title={!available && p.configKey ? 'Connect this calendar first' : ''}
                  >
                    <span className="text-lg">{p.icon}</span>
                    {p.label}
                    {!available && p.configKey && <span className="text-[10px] opacity-70">Not connected</span>}
                  </button>
                );
              })}
            </div>
            {provider && (
              <p className="text-xs text-muted-foreground mt-1.5">
                A calendar event with a video link will be created and invites sent automatically.
              </p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Notes / Instructions (optional)</label>
            <textarea
              className="w-full border rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#003366]"
              rows={3}
              placeholder="Meeting link, location, what to prepare…"
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button
            className="flex-1 bg-[#003366] hover:bg-[#003366]/90"
            onClick={schedule}
            disabled={saving || !interviewDate}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Calendar className="w-4 h-4 mr-1.5" />}
            Schedule Interview
          </Button>
        </div>
      </div>
    </div>
  );
}
